import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "../App";
import { useNavigate } from "react-router-dom";
import { ShieldAlert, TrendingUp, TrendingDown, Minus, DollarSign, Zap, BarChart3, Activity, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLS_COLORS = { healthy: "#22c55e", low_margin: "#f59e0b", underpriced: "#ef4444", insufficient_data: "#6b7280", no_cost_data: "#6b7280" };
const CLS_LABELS = { healthy: "Healthy", low_margin: "Low Margin", underpriced: "Underpriced", insufficient_data: "Insufficient Data", no_cost_data: "No Cost Data" };
const PLAN_COLORS = { free: "#6b7280", pro: "#3b82f6", power: "#8b5cf6" };

function Skeleton({ className = "" }) {
  return <div className={`animate-pulse bg-muted/40 rounded-lg ${className}`} />;
}

function StatCard({ label, value, sub, color = "text-primary", icon: Icon }) {
  return (
    <div className="bg-card border rounded-xl p-5 flex flex-col gap-1" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground uppercase tracking-wider">{label}</span>
        {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
      </div>
      <span className={`text-2xl font-bold tracking-tight ${color}`}>{value}</span>
      {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
    </div>
  );
}

function SectionHeader({ title, sub }) {
  return (
    <div className="mb-4">
      <h2 className="text-lg font-semibold">{title}</h2>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

function Badge({ cls }) {
  const c = CLS_COLORS[cls] || "#6b7280";
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: c + "20", color: c }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: c }} />
      {CLS_LABELS[cls] || cls}
    </span>
  );
}

function TrendIcon({ pct }) {
  if (pct === null || pct === undefined) return <Minus className="w-3.5 h-3.5 text-muted-foreground" />;
  if (pct > 5) return <TrendingUp className="w-3.5 h-3.5 text-red-400" />;
  if (pct < -5) return <TrendingDown className="w-3.5 h-3.5 text-emerald-400" />;
  return <Minus className="w-3.5 h-3.5 text-muted-foreground" />;
}

function fmt(n, d = 2) { return n != null ? `$${Number(n).toFixed(d)}` : "—"; }
function pct(n) { return n != null ? `${Number(n).toFixed(1)}%` : "—"; }
function num(n) { return n != null ? Number(n).toLocaleString() : "—"; }

export default function PricingIntelligence() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [costData, setCostData] = useState(null);
  const [marginData, setMarginData] = useState(null);
  const [calibData, setCalibData] = useState(null);
  const [projectData, setProjectData] = useState(null);

  const isAdmin = user?.is_admin;

  const fetchAll = useCallback(async () => {
    if (!token || !isAdmin) return;
    setLoading(true);
    setError(null);
    const h = { Authorization: `Bearer ${token}` };
    try {
      const [c, m, cal, p] = await Promise.allSettled([
        fetch(`${API}/admin/ai-cost-analytics?days=${days}`, { headers: h }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/admin/ai-margin-report?days=${days}`, { headers: h }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/admin/ai-calibration-report?days=${days}`, { headers: h }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/admin/ai-project-costs?days=${days}`, { headers: h }).then(r => r.ok ? r.json() : null),
      ]);
      setCostData(c.status === "fulfilled" ? c.value : null);
      setMarginData(m.status === "fulfilled" ? m.value : null);
      setCalibData(cal.status === "fulfilled" ? cal.value : null);
      setProjectData(p.status === "fulfilled" ? p.value : null);
    } catch (e) {
      setError("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, [token, days, isAdmin]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Derived data — wrapped in useMemo to maintain stable references for downstream hooks
  const totals = costData?.totals || {};
  const planMargin = useMemo(() => costData?.margin_by_plan || [], [costData]);
  const endpointMargin = costData?.margin_by_endpoint || [];
  const costByUser = costData?.cost_by_user || [];
  const endpoints = useMemo(() => calibData?.endpoints || [], [calibData]);
  const plans = calibData?.plans || [];
  const utilization = useMemo(() => calibData?.utilization || {}, [calibData]);
  const exhaustion = useMemo(() => calibData?.exhaustion || {}, [calibData]);
  const trends = calibData?.trends || {};
  const recs = useMemo(() => calibData?.recommendations || {}, [calibData]);
  const projects = projectData?.projects || [];
  const underpriced = useMemo(() => marginData?.underpriced_workflows || [], [marginData]);
  const healthy = useMemo(() => marginData?.healthy_workflows || [], [marginData]);

  // Endpoint table: merge calibration + margin data, sort underpriced first
  const endpointTable = useMemo(() => {
    const map = {};
    for (const ep of endpoints) {
      map[ep.endpoint] = { ...ep };
    }
    for (const ep of [...underpriced, ...healthy]) {
      const key = ep.endpoint;
      if (map[key]) {
        map[key].total_cost_usd = ep.total_cost_usd;
        map[key].margin_pct = ep.margin_pct;
        map[key].flags = ep.flags;
      }
    }
    const order = { underpriced: 0, low_margin: 1, no_cost_data: 2, insufficient_data: 3, healthy: 4 };
    return Object.values(map).sort((a, b) => (order[a.classification] ?? 5) - (order[b.classification] ?? 5));
  }, [endpoints, underpriced, healthy]);

  // Recommendation map
  const recMap = useMemo(() => {
    const m = {};
    for (const r of recs.endpoint_adjustments || []) m[r.endpoint] = r;
    return m;
  }, [recs]);

  // Charts data
  const planPieData = useMemo(() => planMargin.map(p => ({ name: p.plan, value: p.total_cost_usd || 0 })), [planMargin]);
  const exhaustionPieData = useMemo(() => [
    { name: "Comfortable", value: exhaustion.comfortable_count || 0, color: "#22c55e" },
    { name: "Near Exhaustion", value: exhaustion.near_exhaustion_count || 0, color: "#f59e0b" },
    { name: "Exhausted", value: exhaustion.exhausted_count || 0, color: "#ef4444" },
  ].filter(d => d.value > 0), [exhaustion]);
  const topEndpoints = useMemo(() => (utilization.top_consuming_endpoints || []).slice(0, 8).map(e => ({
    name: e.endpoint.split("/").pop(),
    credits: e.total_credits,
  })), [utilization]);

  // Trend short/long keys
  const trendShort = trends[`last_7d_daily_avg`] || trends[`last_${Math.min(7, days)}d_daily_avg`] || {};
  const trendLong = trends[`last_${days}d_daily_avg`] || trends[`last_30d_daily_avg`] || {};

  // Not admin — unauthorized page
  if (user && !isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="admin-unauthorized">
        <div className="text-center space-y-4">
          <ShieldAlert className="w-12 h-12 text-destructive mx-auto" />
          <h1 className="text-xl font-semibold">Admin Access Required</h1>
          <p className="text-muted-foreground text-sm">This page is restricted to administrators.</p>
          <Button variant="outline" onClick={() => navigate("/dashboard")} data-testid="unauthorized-go-back">Go to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="pricing-intelligence-page">
      <div className="max-w-[1400px] mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid="pi-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Pricing Intelligence</h1>
              <p className="text-sm text-muted-foreground">AI usage, cost, margin & calibration — read-only</p>
            </div>
          </div>
          <div className="flex items-center gap-2" data-testid="pi-filters">
            {[7, 30, 90].map(d => (
              <Button key={d} size="sm" variant={days === d ? "default" : "outline"} onClick={() => setDays(d)} data-testid={`pi-filter-${d}d`}>
                {d}d
              </Button>
            ))}
          </div>
        </div>

        {error && <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 mb-6 text-sm text-destructive">{error}</div>}

        {loading ? (
          <div className="grid grid-cols-4 gap-4 mb-8">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
          </div>
        ) : (
          <div className="space-y-10">
            {/* Section 1: Overview Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="pi-overview">
              <StatCard label="Total AI Cost" value={fmt(totals.total_cost_usd)} sub={`${num(totals.calls_with_cost_data)} calls with cost data`} color="text-foreground" icon={DollarSign} />
              <StatCard label="Total Credits Used" value={num(totals.total_credits)} sub={`${num(totals.total_calls)} total calls`} color="text-foreground" icon={Zap} />
              <StatCard label="Est. Revenue" value={fmt(planMargin.reduce((s, p) => s + (p.effective_revenue_usd || 0), 0))} sub="From plan credit value" color="text-emerald-400" icon={BarChart3} />
              <StatCard label="Est. Margin" value={pct(planMargin.length ? planMargin.reduce((s, p) => s + (p.margin_pct || 0), 0) / planMargin.length : 0)} sub="Avg across plans" color={planMargin.every(p => (p.margin_pct || 0) > 20) ? "text-emerald-400" : "text-amber-400"} icon={Activity} />
            </div>

            {/* Section 2: Revenue & Usage Split */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-card border rounded-xl p-5" data-testid="pi-cost-by-plan">
                <SectionHeader title="Cost by Plan" sub="How AI cost distributes across plans" />
                {planPieData.length ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={planPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, value }) => `${name}: ${fmt(value)}`}>
                        {planPieData.map((e, i) => <Cell key={i} fill={PLAN_COLORS[e.name] || "#6b7280"} />)}
                      </Pie>
                      <Tooltip formatter={v => fmt(v)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <p className="text-sm text-muted-foreground py-8 text-center">No cost data available</p>}
              </div>
              <div className="bg-card border rounded-xl p-5" data-testid="pi-top-endpoints">
                <SectionHeader title="Top Credit Consumers" sub="Endpoints driving credit usage" />
                {topEndpoints.length ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={topEndpoints} layout="vertical" margin={{ left: 80 }}>
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={75} />
                      <Tooltip />
                      <Bar dataKey="credits" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="text-sm text-muted-foreground py-8 text-center">No usage data</p>}
              </div>
            </div>

            {/* Section 3: Plan Profitability */}
            <div data-testid="pi-plan-profitability">
              <SectionHeader title="Plan Profitability" sub="Revenue, cost, and margin per plan" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {plans.map(p => {
                  const marginColor = p.avg_margin_per_user_usd < 0 ? "text-red-400" : p.avg_margin_per_user_usd < p.price_monthly * 0.2 ? "text-amber-400" : "text-emerald-400";
                  return (
                    <div key={p.plan} className="bg-card border rounded-xl p-5" data-testid={`pi-plan-${p.plan}`}>
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-semibold capitalize">{p.plan}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: (PLAN_COLORS[p.plan] || "#6b7280") + "20", color: PLAN_COLORS[p.plan] || "#6b7280" }}>${p.price_monthly}/mo</span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-muted-foreground">Active users</span><span>{p.active_users}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Avg monthly credits</span><span>{num(p.avg_monthly_credits_used)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Avg monthly cost</span><span>{fmt(p.avg_monthly_cost_usd, 4)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">P50 cost</span><span>{fmt(p.p50_monthly_cost_usd, 4)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">P90 cost</span><span>{fmt(p.p90_monthly_cost_usd, 4)}</span></div>
                        <div className="flex justify-between font-medium"><span className="text-muted-foreground">Margin/user</span><span className={marginColor}>{fmt(p.avg_margin_per_user_usd)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Hitting limit</span><span className={p.pct_hitting_limit > 40 ? "text-red-400" : ""}>{pct(p.pct_hitting_limit)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Near exhaustion</span><span className={p.pct_near_exhaustion > 30 ? "text-amber-400" : ""}>{pct(p.pct_near_exhaustion)}</span></div>
                      </div>
                    </div>
                  );
                })}
                {plans.length === 0 && <p className="text-sm text-muted-foreground col-span-3 py-8 text-center">No plan data available</p>}
              </div>
            </div>

            {/* Section 4: Endpoint Profitability Table */}
            <div data-testid="pi-endpoint-table">
              <SectionHeader title="Endpoint Profitability" sub="Sorted by risk: underpriced first" />
              <div className="bg-card border rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-xs text-muted-foreground">
                        <th className="px-4 py-3">Endpoint</th>
                        <th className="px-3 py-3">Credits</th>
                        <th className="px-3 py-3">Avg Cost</th>
                        <th className="px-3 py-3">Ratio</th>
                        <th className="px-3 py-3">Status</th>
                        <th className="px-3 py-3">Confidence</th>
                        <th className="px-3 py-3">Calls</th>
                        <th className="px-3 py-3">Rec.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {endpointTable.slice(0, 30).map((ep, i) => {
                        const rec = recMap[ep.endpoint];
                        return (
                          <tr key={i} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                            <td className="px-4 py-2.5 font-mono text-xs">{ep.endpoint.replace("/api/ai/", "")}</td>
                            <td className="px-3 py-2.5">{ep.current_credits}</td>
                            <td className="px-3 py-2.5">{fmt(ep.avg_cost_usd_trimmed, 4)}</td>
                            <td className="px-3 py-2.5">{ep.cost_to_value_ratio?.toFixed(2) ?? "—"}</td>
                            <td className="px-3 py-2.5"><Badge cls={ep.classification} /></td>
                            <td className="px-3 py-2.5 text-xs capitalize">{ep.confidence}</td>
                            <td className="px-3 py-2.5">{num(ep.call_count)}</td>
                            <td className="px-3 py-2.5">
                              {rec ? (
                                <span className="text-xs" title={rec.reason}>
                                  {rec.current_credits} → <span className="font-semibold text-amber-400">{rec.recommended_credits}</span>
                                </span>
                              ) : "—"}
                            </td>
                          </tr>
                        );
                      })}
                      {endpointTable.length === 0 && (
                        <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">No endpoint data available</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Section 5: Calibration Recommendations */}
            {(recs.endpoint_adjustments?.length > 0 || recs.plan_adjustments?.length > 0) && (
              <div data-testid="pi-recommendations">
                <SectionHeader title="Calibration Recommendations" sub="Advisory only — no auto-changes" />
                <div className="bg-card border rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-xs text-muted-foreground">
                        <th className="px-4 py-3">Target</th>
                        <th className="px-3 py-3">Action</th>
                        <th className="px-3 py-3">Current</th>
                        <th className="px-3 py-3">Recommended</th>
                        <th className="px-3 py-3">Confidence</th>
                        <th className="px-4 py-3">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(recs.endpoint_adjustments || []).map((r, i) => (
                        <tr key={`ep-${i}`} className="border-b border-border/50">
                          <td className="px-4 py-2.5 font-mono text-xs">{r.endpoint?.replace("/api/ai/", "")}</td>
                          <td className="px-3 py-2.5"><span className={`text-xs px-2 py-0.5 rounded-full ${r.action === "increase_credits" ? "bg-red-400/10 text-red-400" : r.action === "consider_increase" ? "bg-amber-400/10 text-amber-400" : "bg-emerald-400/10 text-emerald-400"}`}>{r.action?.replace(/_/g, " ")}</span></td>
                          <td className="px-3 py-2.5">{r.current_credits}</td>
                          <td className="px-3 py-2.5 font-semibold">{r.recommended_credits}</td>
                          <td className="px-3 py-2.5 text-xs capitalize">{r.confidence}</td>
                          <td className="px-4 py-2.5 text-xs text-muted-foreground max-w-xs truncate" title={r.reason}>{r.reason}</td>
                        </tr>
                      ))}
                      {(recs.plan_adjustments || []).map((r, i) => (
                        <tr key={`pl-${i}`} className="border-b border-border/50">
                          <td className="px-4 py-2.5 capitalize font-medium">{r.plan} Plan</td>
                          <td className="px-3 py-2.5"><span className="text-xs px-2 py-0.5 rounded-full bg-amber-400/10 text-amber-400">{r.action?.replace(/_/g, " ")}</span></td>
                          <td className="px-3 py-2.5">${r.current_price || r.current_credits}</td>
                          <td className="px-3 py-2.5">—</td>
                          <td className="px-3 py-2.5 text-xs capitalize">{r.confidence}</td>
                          <td className="px-4 py-2.5 text-xs text-muted-foreground max-w-xs truncate" title={r.reason}>{r.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Section 6: Credit Utilization & Exhaustion */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-card border rounded-xl p-5" data-testid="pi-exhaustion">
                <SectionHeader title="Credit Exhaustion" sub={`${exhaustion.total_users || 0} total users`} />
                {exhaustionPieData.length ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={exhaustionPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, value }) => `${name}: ${value}`}>
                        {exhaustionPieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <p className="text-sm text-muted-foreground py-8 text-center">No exhaustion data</p>}
                {exhaustion.credit_pack_recommendation && (
                  <div className={`mt-3 text-xs p-2 rounded-lg ${exhaustion.credit_pack_recommendation === "strong_signal" ? "bg-red-400/10 text-red-400" : exhaustion.credit_pack_recommendation === "moderate_signal" ? "bg-amber-400/10 text-amber-400" : "bg-muted/30 text-muted-foreground"}`}>
                    {exhaustion.credit_pack_reason}
                  </div>
                )}
              </div>
              <div className="bg-card border rounded-xl p-5" data-testid="pi-utilization">
                <SectionHeader title="Utilization" sub="How users consume credits" />
                <div className="space-y-4 mt-4">
                  <div className="flex justify-between items-center"><span className="text-sm text-muted-foreground">Active users</span><span className="font-semibold">{num(utilization.total_active_users)}</span></div>
                  <div className="flex justify-between items-center"><span className="text-sm text-muted-foreground">Under-utilizers (&lt;10%)</span><span className="font-semibold">{utilization.under_utilizers} ({pct(utilization.under_utilizer_pct)})</span></div>
                  <div className="flex justify-between items-center"><span className="text-sm text-muted-foreground">Heavy utilizers (&gt;80%)</span><span className="font-semibold text-amber-400">{utilization.heavy_utilizers} ({pct(utilization.heavy_utilizer_pct)})</span></div>
                </div>
              </div>
            </div>

            {/* Section 7: Heavy Users */}
            <div data-testid="pi-heavy-users">
              <SectionHeader title="Top Users by Cost" sub="Highest cost users — margin impact" />
              <div className="bg-card border rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-xs text-muted-foreground">
                        <th className="px-4 py-3">User</th>
                        <th className="px-3 py-3">Plan</th>
                        <th className="px-3 py-3">Credits Used</th>
                        <th className="px-3 py-3">Est. Cost</th>
                        <th className="px-3 py-3">Est. Revenue</th>
                        <th className="px-3 py-3">Margin</th>
                        <th className="px-3 py-3">Calls</th>
                      </tr>
                    </thead>
                    <tbody>
                      {costByUser.slice(0, 20).map((u, i) => (
                        <tr key={i} className="border-b border-border/50 hover:bg-muted/30">
                          <td className="px-4 py-2.5 font-mono text-xs">{u.user_id}</td>
                          <td className="px-3 py-2.5 capitalize">{u.plan}</td>
                          <td className="px-3 py-2.5">{num(u.total_credits)}</td>
                          <td className="px-3 py-2.5">{fmt(u.total_cost_usd, 4)}</td>
                          <td className="px-3 py-2.5">{fmt(u.effective_revenue_usd, 4)}</td>
                          <td className={`px-3 py-2.5 font-medium ${u.margin_usd < 0 ? "text-red-400" : "text-emerald-400"}`}>{fmt(u.margin_usd)}</td>
                          <td className="px-3 py-2.5">{num(u.calls)}</td>
                        </tr>
                      ))}
                      {costByUser.length === 0 && (
                        <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">No user cost data available</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Section 8: Project Cost Attribution */}
            <div data-testid="pi-projects">
              <SectionHeader title="Project Cost Attribution" sub="Most expensive projects" />
              <div className="bg-card border rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-xs text-muted-foreground">
                        <th className="px-4 py-3">Project</th>
                        <th className="px-3 py-3">Credits</th>
                        <th className="px-3 py-3">Est. Cost</th>
                        <th className="px-3 py-3">Calls</th>
                        <th className="px-3 py-3">Users</th>
                        <th className="px-3 py-3">Top Endpoints</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projects.slice(0, 15).map((p, i) => (
                        <tr key={i} className="border-b border-border/50 hover:bg-muted/30">
                          <td className="px-4 py-2.5 font-mono text-xs">{p.project_id}</td>
                          <td className="px-3 py-2.5">{num(p.total_credits)}</td>
                          <td className="px-3 py-2.5">{fmt(p.total_cost_usd, 4)}</td>
                          <td className="px-3 py-2.5">{num(p.call_count)}</td>
                          <td className="px-3 py-2.5">{p.unique_users}</td>
                          <td className="px-3 py-2.5 text-xs text-muted-foreground">{(p.endpoint_breakdown || []).slice(0, 2).map(e => e.endpoint.split("/").pop()).join(", ") || "—"}</td>
                        </tr>
                      ))}
                      {projects.length === 0 && (
                        <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">No project cost data available</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Section 9: Trends */}
            <div data-testid="pi-trends">
              <SectionHeader title="Trends" sub={`${Math.min(7, days)}d vs ${days}d daily averages`} />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { label: "Cost/Day", short: trendShort.daily_cost_usd, long: trendLong.daily_cost_usd, trend: trends.cost_trend_pct, format: fmt },
                  { label: "Credits/Day", short: trendShort.daily_credits, long: trendLong.daily_credits, trend: trends.credit_trend_pct, format: num },
                  { label: "Calls/Day", short: trendShort.daily_calls, long: trendLong.daily_calls, trend: trends.call_trend_pct, format: num },
                ].map(t => (
                  <div key={t.label} className="bg-card border rounded-xl p-5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-muted-foreground uppercase tracking-wider">{t.label}</span>
                      <div className="flex items-center gap-1">
                        <TrendIcon pct={t.trend} />
                        <span className={`text-xs font-medium ${t.trend > 5 ? "text-red-400" : t.trend < -5 ? "text-emerald-400" : "text-muted-foreground"}`}>
                          {t.trend != null ? `${t.trend > 0 ? "+" : ""}${t.trend}%` : "—"}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-baseline gap-3">
                      <span className="text-xl font-bold">{t.format(t.short)}</span>
                      <span className="text-xs text-muted-foreground">recent</span>
                    </div>
                    <div className="flex items-baseline gap-3 mt-1">
                      <span className="text-sm text-muted-foreground">{t.format(t.long)}</span>
                      <span className="text-xs text-muted-foreground">{days}d avg</span>
                    </div>
                  </div>
                ))}
              </div>
              {/* Emerging expensive endpoints */}
              {(trends.emerging_expensive_endpoints || []).length > 0 && (
                <div className="mt-4 bg-card border rounded-xl p-5">
                  <h3 className="text-sm font-medium mb-2">Emerging Expensive Endpoints (Recent Period)</h3>
                  <div className="flex flex-wrap gap-2">
                    {(trends.emerging_expensive_endpoints || []).map((e, i) => (
                      <span key={i} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-red-400/10 text-red-400 text-xs font-medium">
                        {e.endpoint.split("/").pop()} — {fmt(e.recent_cost_usd, 4)} ({e.calls} calls)
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Color Legend */}
            <div className="flex items-center gap-6 text-xs text-muted-foreground border-t pt-6 mt-4">
              <span className="font-medium">Legend:</span>
              {Object.entries(CLS_COLORS).filter(([k]) => k !== "no_cost_data").map(([k, c]) => (
                <span key={k} className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: c }} />{CLS_LABELS[k]}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
